import type { MouseEventHandler, ReactNode } from 'react';

export const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

export const Badge = ({
  tone,
  children,
}: {
  tone: 'gold_dark' | 'dark' | 'gold' | 'success';
  children: ReactNode;
}) => {
  const stylesByTone = {
    gold_dark: 'bg-[#C9963F]/15 border border-[#C9963F]/35 text-[#F8C46C]',
    dark: 'bg-white/10 border border-white/15 text-white',
    gold: 'bg-[#FFF8E6] border border-[#C9963F]/35 text-[#C9963F]',
    success: 'bg-[#E8F9F1] border border-[#A7E7C4] text-[#0E9F6E]',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold',
        stylesByTone[tone]
      )}
    >
      {children}
    </span>
  );
};

export const Card = ({ className, children }: { className?: string; children: ReactNode }) => (
  <section
    className={cx('rounded-[16px] border border-[#E9EDF5] bg-white', className)}
  >
    {children}
  </section>
);

export const Button = ({
  variant,
  size = 'md',
  className,
  trailingIcon,
  children,
  onClick,
  disabled = false,
}: {
  variant: 'secondary' | 'gold';
  size?: 'sm' | 'md';
  className?: string;
  trailingIcon?: ReactNode;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}) => {
  const tone =
    variant === 'gold'
      ? 'bg-gradient-to-r from-[#C9963F] to-[#FDCC6D] text-[#1A1205] hover:brightness-105'
      : 'bg-[#F6F8FC] border border-[#E8EAF0] text-[#0A0B14] hover:bg-[#EEF2FA]';
  const spacing = size === 'sm' ? 'px-3.5 py-2 text-[13px]' : 'px-4.5 py-2.5 text-[14px]';

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold transition-all',
        tone,
        spacing,
        className
      )}
    >
      <span>{children}</span>
      {trailingIcon}
    </button>
  );
};

export const FlipClock = ({
  countdown,
}: {
  countdown: { days: number; hours: number; minutes: number; seconds: number };
}) => {
  const parts = [
    { label: 'D', value: countdown.days },
    { label: 'H', value: countdown.hours },
    { label: 'M', value: countdown.minutes },
    { label: 'S', value: countdown.seconds },
  ];

  return (
    <div className='flex items-center gap-2 md:gap-3'>
      {parts.map((part) => (
        <div key={part.label} className='text-center'>
          <div className="relative inline-block min-w-[62px] overflow-hidden rounded-[10px] border border-white/10 bg-[linear-gradient(180deg,#0f1c4d_0%,#0a1438_100%)] px-2 py-2 shadow-[0_10px_28px_-8px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] after:absolute after:left-0 after:right-0 after:top-1/2 after:h-px after:bg-black/40 after:shadow-[0_1px_0_rgba(255,255,255,0.04)] after:content-['']">
            <span className='block text-center text-[26px] font-semibold leading-none text-white tabular-nums'>
              {String(Math.max(part.value, 0)).padStart(2, '0')}
            </span>
          </div>
          <div className='mt-1 text-[10px] uppercase tracking-[0.12em] text-white/60'>{part.label}</div>
        </div>
      ))}
    </div>
  );
};
